import { debounce, isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { captureMessage } from '@/feature/sentry';
import { useCacheUpdater } from './cache';
import { createRequest } from './request';
import type { StreamChangeEvent } from './streamConfig';
import { streamConfig } from './streamConfig';
import type { DataStreamRequest } from './types';
import { getWebSocketUrl } from './util';
import { useWebSocket } from './webSocket';

// Debounce time (in ms) for updating the websocket subscription
const subscriptionDebounce = 500;

/**
 * Manages the websocket connection for data streaming. It serves two purposes:
 *
 * 1. Monitor what data to stream. Upon modification the websocket subscription
 * is updated.
 *
 * 2. Receive websocket messages and update (react-query) caches.
 *
 * @constructor
 */
export const DataStreamManager = () => {
  // Tracking retries manually because react-use-websocket resets its internal
  // count on every successful onOpen event. Our backend still opens the socket
  // before rejecting invalid tickets, so relying on `options.attemptNumber`
  // keeps you stuck at zero.
  const attemptNumber = useRef(0);

  const handleUpdate = useCacheUpdater();
  const { sendMessage } = useWebSocket(getWebSocketUrl, {
    onMessage: async (message: MessageEvent<string>) => {
      const data = JSON.parse(message.data);
      if (data.status === 401) {
        console.log('Invalid ticket');
        return;
      }
      if (data.type === 'error') {
        console.error('WebSocket error message:', data);
        return;
      }
      attemptNumber.current = 0;
      handleUpdate(data);
    },
    onOpen: () => {
      const request = createRequest(streamConfig.getState());
      sendMessage(JSON.stringify(request));
    },
    shouldReconnect: () => {
      if (attemptNumber.current < 3) {
        attemptNumber.current += 1;
        return true;
      }
      captureMessage('Websocket reconnect attempts exceeded');
      console.warn('Websocket reconnect attempts exceeded');
      return false;
    },
    reconnectInterval: () =>
      Math.min(Math.pow(2, attemptNumber.current) * 1000, 10000),
  });

  const handleChangeSubscription = useMemo(() => {
    let active: DataStreamRequest | undefined;

    return debounce((event: StreamChangeEvent) => {
      const request = createRequest(event);
      if (isEqual(request, active)) {
        return;
      }
      sendMessage(JSON.stringify(request));
      active = request;
    }, subscriptionDebounce);
  }, [sendMessage]);

  useEffect(() => {
    streamConfig.on('change', handleChangeSubscription);

    return () => {
      streamConfig.off('change', handleChangeSubscription);
      handleChangeSubscription.cancel();
    };
  }, [handleChangeSubscription]);

  return null;
};
